import CheckIcon from '@mui/icons-material/Check'
import PersonIcon from '@mui/icons-material/Person'
import { Alert, Avatar, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemAvatar, ListItemButton, ListItemText } from '@mui/material'
import { AssignableFollowUpAgent, useAssignFollowUpAgent, useGetAssignableAgents, useUnassignFollowUpAgent } from '@src/data/api/follow-up-agent/follow-up-agent-api'
import { COLOR_TOKENS } from '@src/services/style-tokens'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface AssignFollowUpAgentDialogProps {
  open: boolean
  onClose: () => void
  currentAgent: { id?: string; firstName: string; lastName: string; initials: string } | null
  creditApplicationId: string
  financingProgramId: string
  currentFilters?: Record<string, string | number | string[] | number[]>
}

const AssignFollowUpAgentDialog = ({
  open,
  onClose,
  currentAgent,
  creditApplicationId,
  financingProgramId,
  currentFilters,
}: AssignFollowUpAgentDialogProps) => {
  const { t } = useTranslation()
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)

  const [agents, isLoadingAgents] = useGetAssignableAgents(open)
  const [assignAgent, isAssigning, assignError] = useAssignFollowUpAgent(
    financingProgramId,
    creditApplicationId,
    currentFilters,
  )
  const [unassignAgent, isUnassigning, unassignError] = useUnassignFollowUpAgent(
    financingProgramId,
    creditApplicationId,
    currentFilters,
  )

  useMemo(() => {
    if (open) {
      if (currentAgent?.id) {
        setSelectedAgentId(currentAgent.id)
      } else {
        setSelectedAgentId(null)
      }
    }
  }, [open, currentAgent?.id])

  const handleAssign = async () => {
    if (selectedAgentId === null) {
      // Unassign when "Unassigned" is selected
      await unassignAgent()
    } else {
      // Assign when a real agent is selected
      await assignAgent({ agentId: selectedAgentId })
    }
    onClose()
  }

  const error = assignError || unassignError
  const isLoading = isAssigning || isUnassigning

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('followUpAgent.assignDialogTitle')}</DialogTitle>
      <DialogContent>
        {isLoadingAgents ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <CircularProgress />
          </div>
        ) : agents.length > 0 ? (
          <List>
            <ListItemButton
              key="unassigned"
              selected={selectedAgentId === null}
              onClick={() => setSelectedAgentId(null)}
            >
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: '#e0e0e0' }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={t('followUpAgent.unassigned')}
                secondary={t('followUpAgent.noFollowUpAgent')}
              />
              {selectedAgentId === null && <CheckIcon color="primary" />}
            </ListItemButton>

            {agents.map((agent: AssignableFollowUpAgent) => (
              <ListItemButton
                key={agent.id}
                selected={selectedAgentId === agent.id}
                onClick={() => setSelectedAgentId(agent.id)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: COLOR_TOKENS.addFollowUpAgentIcon }}>
                    {agent.initials}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${agent.firstName} ${agent.lastName}`} />
                {selectedAgentId === agent.id && <CheckIcon color="primary" />}
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Alert severity="warning">
            {t('followUpAgent.noAgentsFound')}
          </Alert>
        )}

        {!!error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('followUpAgent.assignError')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleAssign}
          disabled={selectedAgentId === undefined || isLoading || agents.length === 0}
          variant="contained"
        >
          {isLoading ? t('common.loading') : t('followUpAgent.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignFollowUpAgentDialog
